import { DollarAmount } from '@strata/react-dollar-amount';
import { type ColumnDef, type ColumnDefGeneric, Grid, SecondaryData } from '@strata/react-grid';
import { ClaimStatusIndicator } from '@strata/react-status-indicators';
import classNames from 'classnames';

import { ClaimCodesContainer } from 'components/claims/ClaimCodesContainer';
import { ClaimSubmissionTypes } from 'services/MedicalBillingConstants';
import { type ClaimDetails } from 'store/api/medbillingApi';

const CLAIM_TOTALS_ROW_KEY = 'CLAIM_TOTALS';

interface ServicesOverviewRow {
    procedureCode: string;
    procedureCodeDisplayText: string;
    submittedAmount?: number;
    contractualObligation?: number;
    payerAmount?: number;
    otherAdj?: number;
    memberLiability?: number;
    adjTotal?: number;
    total?: number;
    adjCodes: typeof CLAIM_TOTALS_ROW_KEY | string[];
    serviceStatus?:
        | typeof CLAIM_TOTALS_ROW_KEY
        | (typeof ClaimStatusIndicator.Statuses)[keyof typeof ClaimStatusIndicator.Statuses];
}

const formatGridData = (claimData: ClaimDetails): ServicesOverviewRow[] => [
    ...claimData.services.map((service) => ({
        procedureCode: service.procedureCode,
        procedureCodeDisplayText: service.procedureCodeDisplayText,
        submittedAmount: service.submittedAmount,
        contractualObligation: service.contractualObligation,
        payerAmount: service.payerAmount,
        otherAdj: service.otherAdj,
        memberLiability: service.memberLiability,
        adjTotal: service.adjTotal,
        total: service.total,
        adjCodes: service.adjCodes,
        serviceStatus: service.serviceStatus,
    })),
    {
        procedureCode: CLAIM_TOTALS_ROW_KEY,
        procedureCodeDisplayText: 'Claim Total',
        submittedAmount: claimData.submittedAmount,
        contractualObligation: claimData.contractualObligation,
        payerAmount: claimData.payerAmount,
        otherAdj: claimData.otherAdj,
        memberLiability: claimData.memberLiability,
        adjTotal: claimData.adjTotal,
        total: claimData.claimTotal,
        adjCodes: CLAIM_TOTALS_ROW_KEY,
        serviceStatus: CLAIM_TOTALS_ROW_KEY,
    },
];

const columns = ({
    isEstimate,
    showStatusCol,
    previousEstimate,
}: {
    isEstimate: boolean;
    showStatusCol: boolean;
    previousEstimate?: ClaimDetails;
}): ColumnDef<ServicesOverviewRow>[] => {
    const claimServiceDollarAmount = (
        header: string,
    ): Partial<ColumnDefGeneric<ServicesOverviewRow, 'adjTotal'>> => ({
        size: 100,
        header,
        headerStyle: { justifyContent: 'flex-end' },
        cell: ({ getValue, column, row }) => {
            const { procedureCode } = row.original;

            // Find previous value using procedure code
            const previousRowData =
                procedureCode === CLAIM_TOTALS_ROW_KEY
                    ? previousEstimate
                    : previousEstimate?.services?.find(
                          (service) => service.procedureCode === procedureCode,
                      );
            const previousValue = previousRowData?.[column.id as keyof typeof previousRowData];

            return (
                <div
                    className={classNames('text-align--right', {
                        // If previous value exists & is different display w/ yellow text
                        'sds_claim-totals__grid-row--value-difference':
                            previousValue !== undefined && previousValue !== getValue(),
                        'sds_claim-totals__grid-row--value-total':
                            procedureCode === CLAIM_TOTALS_ROW_KEY,
                    })}
                    style={{ flexGrow: 1 }}
                >
                    <DollarAmount amount={getValue()} showZeroValue />
                </div>
            );
        },
    });

    return [
        {
            accessorKey: 'procedureCodeDisplayText',
            header: 'Service',
            size: 200,
        },
        {
            ...claimServiceDollarAmount('Submitted Amt.'),
            accessorKey: 'submittedAmount',
        },
        {
            ...claimServiceDollarAmount('Contractual Obligation'),
            accessorKey: 'contractualObligation',
            size: 125,
        },
        {
            ...claimServiceDollarAmount('Payer Amt.'),
            accessorKey: 'payerAmount',
        },
        {
            ...claimServiceDollarAmount('Other Adj.'),
            accessorKey: 'otherAdj',
        },
        {
            ...claimServiceDollarAmount('Member Liability'),
            accessorKey: 'memberLiability',
        },
        {
            ...claimServiceDollarAmount('Adj. Total'),
            accessorKey: 'adjTotal',
        },
        {
            ...claimServiceDollarAmount(isEstimate ? 'Est. Payment Amt.' : 'Paid Amt.'),
            accessorKey: 'total',
        },
        {
            accessorKey: 'adjCodes',
            header: 'Adj. Codes',
            size: 125,
            cell: ({ getValue }) => {
                const value = getValue();
                return value !== CLAIM_TOTALS_ROW_KEY ? (
                    <SecondaryData data={(value ?? []).join(', ') || undefined} />
                ) : null;
            },
        },
        ...(showStatusCol
            ? [
                  {
                      accessorKey: 'serviceStatus',
                      header: 'Status',
                      size: 125,
                      cell: ({ getValue }) => {
                          const value = getValue();
                          if (value === CLAIM_TOTALS_ROW_KEY) {
                              return null;
                          }

                          return value ? (
                              <ClaimStatusIndicator status={value} />
                          ) : (
                              <SecondaryData />
                          );
                      },
                  } as ColumnDef<ServicesOverviewRow>,
              ]
            : []),
    ];
};

interface ClaimTotalsGridAndCodesProps {
    claimData?: ClaimDetails;
    previousEstimate?: ClaimDetails;
    showStatusCol?: boolean;
}

export const ClaimTotalsGridAndCodes = ({
    claimData = undefined,
    previousEstimate = undefined,
    showStatusCol = false,
}: ClaimTotalsGridAndCodesProps) => {
    const isEstimate = claimData?.submissionType === ClaimSubmissionTypes.ESTIMATE;
    return (
        <div className="sds_claim-totals">
            {claimData && (claimData.services ?? []).length > 0 && (
                <Grid
                    data={formatGridData(claimData)}
                    enableSorting={false}
                    columns={columns({ isEstimate, showStatusCol, previousEstimate })}
                />
            )}
            <ClaimCodesContainer
                className="margin__top--large"
                categories={[{ label: 'Adjustment Codes', codes: claimData?.adjCodes }]}
            />
        </div>
    );
};
